import React, {
  Fragment,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { Combobox, Transition } from "@headlessui/react";
import clsx from "clsx";
import { ComboboxOption } from "../../types";
import Chip from "./chip";
import {
  faCheck,
  faChevronDown,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface ComboboxProps {
  comboboxType: "select" | "search";
  options: ComboboxOption[];
  placeholder?: string;
  valueProperty: string;
  labelProperty: string;
  value?: ComboboxOption | ComboboxOption[];
  error?: string;
  label?: string | JSX.Element;
  disabled?: boolean;
  name?: string;
  multiple?: boolean;
  isLoading?: boolean;
  filterOptions?: (query: string) => void;
  onSingleSelectChange?: (value: ComboboxOption | null) => void;
  onMultiSelectChange?: (value: ComboboxOption[] | null) => void;
  colorSelect?: boolean;
  inputClass?: string;
  hideSelectedOptionsChips?: boolean;
  showTop?: boolean;
  hideChip?: boolean;
  onInputChange?: (value: string) => void;
  hideOnFocus?: boolean;
}

const CustomSelect = forwardRef<HTMLSelectElement, ComboboxProps>(
  (
    {
      comboboxType,
      options,
      valueProperty,
      labelProperty,
      filterOptions,
      placeholder,
      label,
      disabled,
      name,
      error,
      value,
      multiple,
      onSingleSelectChange,
      onMultiSelectChange,
      colorSelect,
      inputClass,
      hideSelectedOptionsChips,
      isLoading,
      showTop = false,
      hideChip = false,
      onInputChange,
      hideOnFocus = false,
    },
    ref
  ) => {
    const [query, setQuery] = useState("");
    const [filteredOptions, setFilteredOptions] =
      useState<ComboboxOption[]>(options);
    useEffect(() => {
      setFilteredOptions(options);
    }, [options]);

    const handleFilterQuery = async (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { value } = e.target;
      setQuery(value);
      if (comboboxType === "select") {
        const filtered =
          value === ""
            ? options
            : options.filter((option) => {
                return option?.[labelProperty]
                  ?.toLowerCase()
                  .includes(value.toLowerCase());
              });
        setFilteredOptions(filtered);
      } else {
        await filterOptions?.(e.target.value);
      }
    };

    const parsedOptions = JSON.parse(JSON.stringify(options));

    const [selectedIcon, setSelectedIcon] = useState<string | null>(null);

    useEffect(() => {
      if (value && options) {
        const selectedOption = Array.isArray(value) ? value[0] : value; // Adjust based on your handling of multiple selections
        const option = options?.find(
          (option) =>
            option?.[labelProperty] === selectedOption?.[labelProperty]
        );
        setSelectedIcon(option?.img || null);
      }
    }, [value, options, labelProperty]);

    parsedOptions?.forEach((option: any) => {
      if (option?.name === (value as ComboboxOption)?.[labelProperty]) {
      }
    });
    const renderChips = () => {
      if (!hideSelectedOptionsChips) {
        if (multiple && Array.isArray(value)) {
          return value.map((item, index) => (
            <Chip
              key={index}
              result={item[labelProperty]}
              onClick={() => {
                const newValues = value.filter(
                  (val) => val[valueProperty] !== item[valueProperty]
                );
                onMultiSelectChange?.(newValues);
              }}
            />
          ));
        } else if (value) {
          return (
            <Chip
              result={value[labelProperty]}
              onClick={() => onSingleSelectChange?.(null)}
            />
          );
        }
      }
    };

    return (
      <Combobox
        value={value || ""}
        onChange={multiple ? onMultiSelectChange : onSingleSelectChange}
        disabled={disabled}
        name={name}
        ref={ref}
        by={valueProperty}
        multiple={multiple as any}
        nullable
      >
        <div className="space-y-1">
          {label ? (
            <Combobox.Label
              className="text-label-muted font-medium block text-sm leading-[16.98px]"
              data-testid="combobox-label"
            >
              {label}
            </Combobox.Label>
          ) : null}
          <div className="relative">
            <div className="w-full overflow-hidden text-left">
              <Combobox.Button className="w-full">
                <Combobox.Input
                  autoComplete="do-not-autofill"
                  data-testid="combobox-input"
                  type="select"
                  className={clsx(
                    "w-full py-2 pl-3 pr-10 text-sm text-text-input font-bold border border-input-border rounded-lg bg-input-bg transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30",
                    error && "border-red-500",
                    disabled && "opacity-50",
                    colorSelect &&
                      "pl-7 pr-4 !font-medium !text-[11px]",
                    inputClass
                  )}
                  placeholder={placeholder}
                  onChange={(e) => {
                    if (onInputChange) {
                      onInputChange(e.target.value);
                    } else {
                      handleFilterQuery(e);
                    }
                  }}
                  onFocus={(e) => {
                    if (hideOnFocus) {
                      e.target.value = "";
                      onInputChange("");
                    }
                  }}
                  displayValue={() =>
                    !Array.isArray(value) ? value?.[labelProperty] || "" : ""
                  }
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-light-gray">
                  {isLoading ? (
                    <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 mr-2" />
                  )}
                </div>

                {colorSelect && selectedIcon ? (
                  <div className="absolute inset-y-0  left-2 flex items-center pr-2 text-sm">
                    <img
                      src={selectedIcon}
                      alt="Selected"
                      className="w-4 h-4 rounded-[50%]"
                    />
                  </div>
                ) : null}
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery("")}
            >
              <Combobox.Options
                className={clsx(
                  "absolute mt-2 max-h-60 w-full overflow-auto rounded-xl bg-surface-elevated text-sm shadow-card border border-edge z-10 custom-scrollbar",
                  colorSelect && "!mt-0",
                  showTop && "bottom-12"
                )}
              >
                {filteredOptions?.length === 0 && query !== "" ? (
                  <div className="relative cursor-default select-none py-2 px-4 text-light-gray">
                    Nothing found.
                  </div>
                ) : (
                  filteredOptions.map((option, index: number) => (
                    <Combobox.Option
                      data-testid="combobox-option"
                      key={index}
                      className={({ active, selected }) =>
                        clsx(
                          "relative flex gap-2 text-heading-color font-medium cursor-default select-none py-2 px-4 transition-colors duration-150",
                          selected && "bg-overlay-mid",
                          active && "bg-overlay",
                          index !== filteredOptions?.length - 1 &&
                            "border-b border-edge-faint"
                        )
                      }
                      value={option}
                    >
                      {({ selected }) => (
                        <>
                          {colorSelect ? (
                            <div
                              key={index}
                              className="bg-inherit flex items-center gap-2"
                            >
                              <img
                                className="w-[20px] h-[20px] rounded-[50px] object-contain"
                                src={option.img}
                                alt=""
                              />
                            </div>
                          ) : null}
                          <div className="flex w-full justify-between">
                            <span className="block truncate font-medium">
                              {option[labelProperty]}
                            </span>
                            {selected ? (
                              <span className="flex text-blue items-center justify-end pl-3">
                                <FontAwesomeIcon icon={faCheck} />
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
          {!hideChip &&
          multiple &&
          (value?.length || Object.keys(value || {})?.length) ? (
            <div className="pt-3 flex gap-3 flex-wrap">{renderChips()}</div>
          ) : null}
          {error ? (
            <p
              data-testid="combobox-error"
              className="text-red-400 text-xs font-medium"
            >
              {error}
            </p>
          ) : null}
        </div>
      </Combobox>
    );
  }
);

CustomSelect.displayName = "Select";

export default CustomSelect;
